import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './SolicitantesModal';
import SolicitantesTabs from './SolicitantesTabs';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import sigmaDelete from '../../../../Pages/img/Borrar.png';

const PAGE_SIZE = 10;
const nombreTabla = "Solicitantes";
const endpoint = ENDPOINTS.solicitantes;
const endpointById = ENDPOINTS.solicitantesById;
const modelo = Models.solicitantes;

// 🔗 catálogos para enriquecer empleado + unidad organizacional
const endpointEmpleado = ENDPOINTS.empleados;
const modeloEmpleado = Models.empleados;

const endpointPersona = ENDPOINTS.personas;
const modeloPersona = Models.persona;

const endpointUnidades = ENDPOINTS.unidadesOrg;
const modeloUnidad = Models.unidadesOrg;

const maps = {
  mapCreate: (f) => ({
    idEmpleado: Number(f.idEmpleado) || 0,
    idUnidadOrganizacional: Number(f.idUnidadOrganizacional) || 0,
    activo: !!f.activo,
  }),
  mapUpdate: (id, f) => ({
    idSolicitante: id,
    idEmpleado: Number(f.idEmpleado) || 0,
    idUnidadOrganizacional: Number(f.idUnidadOrganizacional) || 0,
    activo: !!f.activo,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resSol, resEmps, resPers, resUnidades] = await Promise.all([
        axios.get(endpoint),
        axios.get(endpointEmpleado),
        axios.get(endpointPersona),
        axios.get(endpointUnidades),
      ]);

      let solicitantes = (resSol.data || []).map((x) => modelo(x));
      const empleados = (resEmps.data || []).map((x) => modeloEmpleado(x));
      const personas = (resPers.data || []).map((x) => modeloPersona(x));
      const unidades = (resUnidades.data || []).map((x) => modeloUnidad(x));

      // Enriquecer cada solicitante con:
      // - empleadoNombre
      // - empleadoNumero
      // - unidadNombre
      // - unidadClave
      solicitantes = solicitantes.map((s) => {
        const emp = empleados.find((e) => e.idEmpleado === s.idEmpleado);
        const per = emp
          ? personas.find((p) => p.idPersona === emp.idPersona)
          : null;
        const uni = unidades.find(
          (u) => u.idUnidadOrganizacional === s.idUnidadOrganizacional
        );

        const fullName = per
          ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
          : '';

        const numEmp = emp?.numeroEmpleado || '';

        const unidadNombre = "";

        const unidadClave = "";

        return {
          ...s,
          empleadoNombre: fullName,
          empleadoNumero: numEmp,
          unidadNombre,
          unidadClave,
        };
      });

      setRows(solicitantes);
    } catch (e) {
      console.error(e);
      alert('Error al cargar información');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const empId = String(r.idEmpleado ?? '').toLowerCase();
      const empNom = (r.empleadoNombre || '').toLowerCase();
      const empNum = (r.empleadoNumero || '').toLowerCase();
      const uniId = String(r.idUnidadOrganizacional ?? '').toLowerCase();
      const uniNom = (r.unidadNombre || '').toLowerCase();
      const uniClave = (r.unidadClave || '').toLowerCase();
      const act = r.activo ? 'si' : 'no';
      return (
        empId.includes(q) ||
        empNom.includes(q) ||
        empNum.includes(q) ||
        uniId.includes(q) ||
        uniNom.includes(q) ||
        uniClave.includes(q) ||
        act.includes(q)
      );
    });
  }, [rows, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idSolicitante === id);
    if (!r) {
      alert('No se encontró la fila en memoria.');
      return;
    }
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idSolicitante ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      alert('Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >{label}</button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            className="roles-search"
            placeholder="Buscar por empleado, unidad organizacional, clave o activo…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>No. Empleado</th>
                  <th>ID Unidad Org</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => {
                  const empleadoLabel = r.empleadoNombre
                    ? r.empleadoNombre
                    : `Empleado #${r.idEmpleado}`;
                  return (
                    <tr key={r.idSolicitante}>
                      <td><strong>{normalizeValue(empleadoLabel)}</strong></td>
                      <td>{normalizeValue(r.empleadoNumero)}</td>
                      <td>{r.idUnidadOrganizacional || 'N/A'}</td>

                      {/* 🔽 Botón EDITAR con ícono */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--edit"
                          onClick={() => handleEdit(r.idSolicitante)}
                          aria-label="Editar"
                          title="Editar"
                        >
                          <img src={sigmaEdit} alt="" className="rk-ico" />
                        </button>
                      </td>

                      {/* 🔽 Botón BORRAR con ícono */}
                      <td style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="rk-iconbtn rk-iconbtn--del"
                          onClick={() => {
                            setEditingRowId(null);
                            const empInfo = r.empleadoNombre
                              ? `${r.empleadoNumero ? r.empleadoNumero + ' - ' : ''}${r.empleadoNombre}`
                              : `Empleado ${r.idEmpleado}`;
                            askDelete(r.idSolicitante, empInfo);
                          }}
                          aria-label="Borrar"
                          title="Borrar"
                        >
                          <img src={sigmaDelete} alt="" className="rk-ico" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={5}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
          </div>
        </div>

        {
          modalOpen && (
            <div className="roles-editor-slot" ref={editorRef}>
              {modalMode === 'new' && (
                <div className="person-tabs-disabled" style={{ marginBottom: 12 }}>
                  <span>
                    Selecciona o crea un solicitante y pulsa <b>Editar</b> para gestionar sus datos
                    relacionados.
                  </span>
                </div>
              )}
              <Modal
                open={modalOpen}
                mode={modalMode}
                data={editData}
                inline
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
              />
              {modalMode === 'edit' && editingRowId > 0 && (
                <SolicitantesTabs solicitanteId={editingRowId} />
              )}
            </div>
          )
        }
      </div >

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}

